import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { Order } from '../../../types';
import { DataTableHeadCellData, DataTableHeader } from './DataTableHeader';
import { DataTableToolbar } from './DataTableToolBar';
import { CircularProgress } from '@mui/material';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aVal: string = extractText(a[orderBy] ?? '');
  const bVal: string = extractText(b[orderBy] ?? '');
  if (aVal < bVal) {
    return -1;
  }
  if (aVal > bVal) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function extractText(element: any): string {
  if (!element) return '';

  if (typeof element === 'string') {
    return element;
  }

  if (element && React.isValidElement(element)) {
    // @ts-ignore
    const childs: any = React.Children.map(element.props.children, extractText);
    if (!childs) return '';
    else return childs.join(' ');
  }

  return '';
}

function search(data: any[], searchKeys: string[], searchQuery: string) {
  if (searchKeys.length == 0) return data;

  const result: any[] = [];

  for (let i=0; i<data.length; i++) {
    let flag = false;
    for (let j=0; j<searchKeys.length; j++) {
      const val: string = extractText(data[i]?.[searchKeys[j]] ?? '');
      if (val.toLowerCase().includes(searchQuery.toLowerCase())) {
        flag = true;
        break;
      }
    }
    if (flag) result.push(data[i]);
  }

  return result;
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface DataTableProps {
  headCells: DataTableHeadCellData[],
  rows: any[],
  title?: string;
  hideToolBar?: boolean;
  onBulkDelete?: (ids: number[]) => void;
  isLoading?: boolean;
  hideIdCheckbox?: boolean;
  hideFilter?: boolean;
  hideDeleteButton?: boolean;
  toolbarAdditionalRightElement?: React.ReactElement;
}

export const DataTable = ({ 
  headCells, 
  rows, 
  title, 
  hideToolBar, 
  isLoading, 
  onBulkDelete,
  hideIdCheckbox,
  hideFilter,
  hideDeleteButton,
  toolbarAdditionalRightElement,
}: DataTableProps) => {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<any>('calories');
  const [selected, setSelected] = React.useState<number[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setSelected([]);
  }, [rows, searchQuery]);

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(() => {
    const searchAbleKeys = headCells.filter((item) => item.searchable).map((item) => item.id);
    const filteredRows = search(rows, searchAbleKeys, searchQuery);
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    )},
    [order, orderBy, page, rows, rowsPerPage, searchQuery],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {
          !hideToolBar && (
            <DataTableToolbar 
              title={title} 
              selected={selected} 
              onDelete={onBulkDelete} 
              onSearch={setSearchQuery} 
              hideFilter={hideFilter} 
              hideDeleteButton={hideDeleteButton} 
              toolbarAdditionalRightElement={toolbarAdditionalRightElement} 
            />
          )
        }
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <DataTableHeader
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={headCells}
              hideIdCheckbox={hideIdCheckbox}
            />
            <TableBody>
              {
                isLoading ? (
                  <TableRow
                    style={{
                      height: 200,
                    }}
                  >
                    <TableCell colSpan={headCells.length + 1} align='center' >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {visibleRows.map((row: any, index) => {
                      const isItemSelected = isSelected(row?.id);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.id}
                          selected={isItemSelected}
                        >
                          {
                            !hideIdCheckbox && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  onClick={(event) => handleClick(event, row?.id)}
                                  inputProps={{
                                    'aria-labelledby': labelId,
                                  }}
                                />
                              </TableCell>
                            )
                          }
                          {
                            headCells.map((key: any, index: number) => {
                              return index === 0 ? (
                                <TableCell
                                  component="th"
                                  id={labelId}
                                  scope="row"
                                  sx={{ paddingLeft: hideIdCheckbox ? "20px" : "0px"  }} 
                                  key={index}
                                >
                                  {row?.[key.id]}
                                </TableCell>
                              ) : (
                                <TableCell 
                                  key={index} 
                                  sx={{ paddingLeft: '0px', paddingRight: '0px' }} 
                                  align={ key?.cellAlign ?? 'center' }
                                >
                                  {row?.[key.id]}
                                </TableCell>
                              )
                            })
                          }
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={headCells.length + 1} />
                      </TableRow>
                    )}
                  </>
                )
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
